import React, { useEffect, useState, useRef } from "react";
import { MapContainer, GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getGeoRegionalState, getGeoWoreda } from "../../services/apiServices.js";
// Endemicity category colors
const categoryColors = {
  "<1% prevalence (non-endemic)": "#e6f2ff",
  "Surveillance (prevalence <1%)": "#cce6ff",
  "1 - 9.9% post-IA prevalence": "#99ccff",
  "1 - 9.9% prevalence (low)": "#66b2ff",
  "10 - 49.9% post-IA prevalence": "#3399ff",
  "10 - 49.9% prevalence (moderate)": "#0073e6",
  ">=50% post-IA prevalence": "#0052b3",
  ">=50% prevalence (high)": "#003366",
  "Endemicity unknown": "#99b3cc",
  "No data available": "#cce0ff",
};

// Default styles for woredas and regions
const woredaStyle = { color: "#009933", weight: 1, fillOpacity: 0.6 };
const regionStyle = { color: "#003366", weight: 1.5, fillOpacity: 0.1 };
const highlightStyle = { color: "#ff6600", weight: 2, fillOpacity: 0.6 };

export default function EndemicityMap() {
  const [regions, setRegions] = useState(null);
  const [woredas, setWoredas] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const mapRef = useRef();

  // Fetch geo data (regions and woredas)
  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch regional data
        const regionalData = await getGeoRegionalState();
        setRegions({
          type: "FeatureCollection",
          features: regionalData.map((r) => ({
            type: "Feature",
            geometry: r.geometry,
            properties: { REGIONNAME: r.regionalState },
          })),
        });

        // Fetch woreda data
        getGeoWoreda(100, (batchData) => {
          const processed = batchData.map((w) => ({
            ...w,
            properties: {
              ...w.properties,
            },
          }));
          setWoredas({ type: "FeatureCollection", features: processed });
        });
      } catch (err) {
        console.error("Failed to fetch geo data:", err);
      }
    }

    fetchData();
  }, []);

  // Region Layer
  const RegionLayer = ({ data }) => {
    const map = useMap();
    const onEachFeature = (feature, layer) => {
      layer.setStyle(regionStyle);
      layer.on({
        mouseover: () =>
          layer.setStyle({ color: "#ffa500", weight: 2, fillOpacity: 0.3 }),
        mouseout: () => layer.setStyle(regionStyle),
      });
      layer.bindTooltip(feature.properties.REGIONNAME, {
        permanent: true,
        direction: "center",
        className: "region-tooltip",
      });
      layer.bindPopup(`<b>${feature.properties.REGIONNAME}</b>`);
    };
    return <GeoJSON data={data} onEachFeature={onEachFeature} pane="regionPane" />;
  };

  // Woreda Layer
  const WoredaLayer = ({ data, selectedCategories }) => {
    const onEachFeature = (feature, layer) => {
      let endemicity = feature.properties.factors.endemicity;
      let normalizedEndemicity = endemicity ? endemicity.trim() : "No data available";
      const matchesFilter = selectedCategories.length === 0 || selectedCategories.includes(normalizedEndemicity);

      const originalStyle = {
        ...woredaStyle,
        fillColor: matchesFilter
          ? categoryColors[normalizedEndemicity] || "#cccccc"
          : "#dddddd", // grey out non-matching items
        fillOpacity: matchesFilter ? 1 : 0.2, // dim non-matching items
      };
      layer.setStyle(originalStyle);
      layer.on({
        mouseover: () => {
          if (matchesFilter) layer.setStyle(highlightStyle);
          layer.openPopup();
        },
        mouseout: () => layer.setStyle(originalStyle),
      });

      // Popup content
      layer.bindPopup(
        `<b>${feature.properties.WOREDANAME}</b><br/>
      Region: ${feature.properties.REGIONNAME || "Unknown"}<br/>
      Zone: ${feature.properties.ZONENAME || "Unknown"}<br/>
      Endemicity: ${normalizedEndemicity}<br/>`
      );
    };

    return (
      <GeoJSON
        key={selectedCategories.join(",")} // Key by selected categories to trigger re-render
        data={data}
        onEachFeature={onEachFeature}
        pane="woredaPane"
      />
    );
  };


  // Legend component to filter endemicity categories
  const Legend = () => {
    const map = useMap();

    useEffect(() => {
      const legend = L.control({ position: "bottomright" });

      legend.onAdd = () => {
        const div = L.DomUtil.create("div", "info legend");
        div.style.background = "#fff";
        div.style.padding = "16px";
        div.style.borderRadius = "5px";
        div.style.fontFamily = "Poppins, sans-serif";
        div.style.fontSize = "14px";
        div.innerHTML = `<p style="font-weight:700; margin-top:0;">Endemicity Categories (2023)</p>`;
        // Clear filter button
        if (selectedCategories.length > 0) {
          const clearFilter = document.createElement("div");
          clearFilter.style.cursor = "pointer";
          clearFilter.style.marginBottom = "8px";
          clearFilter.style.color = "#024795";
          clearFilter.style.fontWeight = "600";
          clearFilter.innerText = "Clear Filter";
          clearFilter.onclick = () => setSelectedCategories([]);
          div.appendChild(clearFilter);
        }
        // Render category items
        for (const [category, color] of Object.entries(categoryColors)) {
          const item = document.createElement("div");
          item.style.cursor = "pointer";
          item.style.marginBottom = "4px";
          item.style.textDecoration = selectedCategories.includes(category) ? "underline" : "none";
          item.innerHTML =
            `<i style="background:${color}; width:18px; height:18px; float:left; margin-right:8px; opacity:0.7; border-radius:3px;"></i>` +
            category;
          // Toggle category selection
          item.onclick = () => {
            setSelectedCategories((prev) => {
              if (prev.includes(category)) {
                return prev.filter((item) => item !== category); // Deselect category
              } else {
                return [...prev, category]; // Select category
              }
            });
          };
          div.appendChild(item);
        }

        return div;
      };

      legend.addTo(map);
      return () => legend.remove();
    }, [map, selectedCategories]);

    return null;
  };

  // Map Layers component
  const MapLayers = () => {
    const map = useMap();

    useEffect(() => {
      // Ensure map panes for regions and woredas exist
      if (!map.getPane("regionPane")) {
        map.createPane("regionPane");
        map.getPane("regionPane").style.zIndex = 400;
      }
      if (!map.getPane("woredaPane")) {
        map.createPane("woredaPane");
        map.getPane("woredaPane").style.zIndex = 450;
      }
    }, [map]);

    return (
      <>
        {regions && <RegionLayer data={regions} />}
        {woredas && <WoredaLayer data={woredas} selectedCategories={selectedCategories} />}
        <Legend />
      </>
    );
  };

  return (
    <div style={{ height: "100vh", width: "100%" }}>
      <MapContainer
        center={[9.145, 40.4897]}
        zoom={6}
        scrollWheelZoom
        style={{ height: "100%", width: "100%" }}
        ref={mapRef}
      >
        <MapLayers />
      </MapContainer>
    </div>
  );
}
